import axiosPrivate from 'config/axiosPrivate';
import React, { useEffect, useState } from 'react';
import { utils as xlsxUtils, writeFile as xlsxWriteFile } from 'xlsx';
import he from 'he';
import { saveAs } from 'file-saver';
import { fileNameSanitizer } from 'helpers/FileNameSanitizer';

import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { adminOrderDisplaySelector } from 'store/Admin/OrderDisplay';
import { fetchAdminTextOrderDisplay } from 'store/Admin/OrderDisplay/features';

import { stripHTML } from 'utils/StringUtils';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import DownloadModal from 'components/shared/modal/DownloadModal';

const getStatusFromText = (text) => {
  if (text.deployed === true) {
    return 'En ligne';
  } else if (text.validated === true) {
    return 'Validé';
  } else if (text.delivered === true) {
    return 'Livré';
  } else {
    return 'Brouillon';
  }
};

const getWritingAxisFromText = (text) =>
  text?.writingAxis ?? text?.writing_axis ?? text?.axeRedaction ?? '';

const sanitizeHtmlCell = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\n/g, '<br />');

const buildHtmlTableString = (rows) => {
  if (!rows.length) return '<table><thead><tr></tr></thead><tbody></tbody></table>';
  const headers = Object.keys(rows[0]);
  const head = headers.map((h) => `<th>${sanitizeHtmlCell(h)}</th>`).join('');
  const body = rows
    .map((row) => `<tr>${headers.map((h) => `<td>${sanitizeHtmlCell(row[h])}</td>`).join('')}</tr>`)
    .join('');
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/></head><body><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`;
};

const normalizeZipFileName = (name) => {
  const base = (name || 'export_typix').replace(/\.xlsx$/i, '').replace(/\.zip$/i, '');
  return `${base}.zip`;
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const downloadZipFromTexts = async (texts, fallbackFileName) => {
  const zip = require('jszip')();
  const files = await Promise.all(
    (Array.isArray(texts) ? texts : []).map(async (text, index) => {
      try {
        const keywords = text?.title || text?.keyword || text?.textTitle || 'Sans titre';
        const titleTag = text?.titleTag || '';
        const metaDescription = text?.metaDescriptionTag || '';
        const url = text?.url || text?.websiteUrl || '';

        let htmlContent = '';
        if (text?.actualHtmlContent) {
          htmlContent = he.decode(text.actualHtmlContent);
        } else {
          const textContent =
            text?.actualTextContent || text?.content || text?.text || '';
          htmlContent = escapeHtml(textContent).replace(/\n/g, '<br/>');
        }

        const htmlString = `
      <html>
        <head>
          <meta charset="utf-8">
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            h1 {
              font-size: 18pt;
              font-weight: bold;
              margin-bottom: 30px;
              color: #333;
              text-align: center;
            }
            .metadata {
              margin-bottom: 30px;
              padding: 15px;
              background-color: #f5f5f5;
              border-left: 4px solid #333;
            }
            .metadata-item {
              margin-bottom: 10px;
              font-size: 11pt;
            }
            .metadata-label {
              font-weight: bold;
              color: #555;
              display: inline-block;
              width: 150px;
            }
            .metadata-value {
              color: #000;
            }
            p, div {
              font-size: 12pt;
              line-height: 1.6;
              color: #000;
              text-align: justify;
            }
            strong, b {
              font-weight: bold;
            }
            em, i {
              font-style: italic;
            }
            u {
              text-decoration: underline;
            }
            [style*="color"] {
              color: inherit;
            }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(keywords)}</h1>
          
          <div class="metadata">
            ${titleTag ? `
              <div class="metadata-item">
                <span class="metadata-label">Titre :</span>
                <span class="metadata-value">${escapeHtml(titleTag)}</span>
              </div>
            ` : ''}
            ${metaDescription ? `
              <div class="metadata-item">
                <span class="metadata-label">Meta-description :</span>
                <span class="metadata-value">${escapeHtml(metaDescription)}</span>
              </div>
            ` : ''}
            ${url ? `
              <div class="metadata-item">
                <span class="metadata-label">URL :</span>
                <span class="metadata-value">${escapeHtml(url)}</span>
              </div>
            ` : ''}
          </div>
          
          <div>${htmlContent}</div>
        </body>
      </html>
    `;

        const safeBaseName = fileNameSanitizer(keywords) || 'Sans_titre';
        const uniqueSuffix = text?.id ? `-${text.id}` : `-${index + 1}`;
        const docFileName = `${safeBaseName}${uniqueSuffix}.docx`;
        const res = await axiosPrivate({
          url: '/user/external/download-file',
          responseType: 'arraybuffer',
          data: {
            fileName: docFileName,
            htmlString,
          },
          headers: { 'Content-Type': 'application/json' },
          method: 'POST',
        });

        if (res.status !== 200) return null;
        return { name: docFileName, data: new Blob([res.data]) };
      } catch (error) {
        return null;
      }
    })
  );

  const validFiles = files.filter(Boolean);
  if (!validFiles.length) throw new Error('Aucun fichier valide');
  validFiles.forEach((f) => zip.file(f.name, f.data));
  const zipContent = await zip.generateAsync({ type: 'blob' });
  saveAs(zipContent, normalizeZipFileName(fallbackFileName));
};

export const exportTextsByIdsAsZip = async (
  textIds,
  { fileName = 'Export - Typix.zip' } = {}
) => {
  const ids = (Array.isArray(textIds) ? textIds : []).filter(Boolean);
  if (!ids.length) {
    throw new Error('Aucun texte à exporter');
  }
  const response = await axiosPrivate.post('/texts/external/for-export/by-texts', ids);
  await downloadZipFromTexts(response?.data, fileName);
};

export const ExcelExportButton = ({ orderId, data, fileName, columns, textIds }) => {
  const dispatch = useDispatch();
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const { t } = useTranslation();

  let { textList } = useSelector(adminOrderDisplaySelector);
  useEffect(() => {
    if (!textList && orderId) {
      dispatch(fetchAdminTextOrderDisplay());
    }
  }, [dispatch, textList, orderId]);

  async function exportTextExcel() {

    if (Array.isArray(textIds) && textIds.length > 0) {
      const timeout = setTimeout(() => setShowDownloadModal(true), 500);
      exportTextsByIdsAsZip(textIds, { fileName: fileName || 'Export - Typix.zip' })
        .catch((error) => {
          console.error('Error exporting selected texts', error);
          setErrorSuccessMsg(t("Une erreur est survenue lors de l'exportation."));
          setIsSuccess(false);
          setErrorSuccess(true);
        })
        .finally(() => {
          clearTimeout(timeout);
          setShowDownloadModal(false);
        });
      return;
    }


    if (data && Array.isArray(data)) {
      try {
        const timeout = setTimeout(() => setShowDownloadModal(true), 500);
        await downloadZipFromTexts(data, 'Export.zip');
        clearTimeout(timeout);
        setShowDownloadModal(false);
      } catch (error) {
        console.error('Error exporting data', error);
        setShowDownloadModal(false);
        setErrorSuccessMsg(t("Une erreur est survenue lors de l'exportation."));
        setIsSuccess(false);
        setErrorSuccess(true);
      }
      return;
    }

    if (!orderId) {
      setErrorSuccessMsg(t('Aucun identifiant fourni pour l\'exportation.'));
      setIsSuccess(false);
      setErrorSuccess(true);
      return;
    }

    const timeout = setTimeout(() => setShowDownloadModal(true), 500);
    axiosPrivate
      .get(`/texts/external/for-export/by-project/${orderId}`)
      .then(async (res) => {
        const textLengthExceeded = res.data.some((text) => {
          if (
            text.actualTextContent?.length >= 32767 ||
            text.actualHtmlContent?.length >= 32767
          ) {
            clearTimeout(timeout);
            return true;
          }
          return false;
        });

        if (textLengthExceeded) {
          setErrorSuccessMsg(
            t('Un texte est trop long et ne peut pas être exporté sur Excel.')
          );
          setIsSuccess(false);
          setErrorSuccess(true);
          clearTimeout(timeout);
          setShowDownloadModal(false);
          return;
        } else {
          await downloadZipFromTexts(res.data, 'Export roadmap Typix.zip');
          clearTimeout(timeout);
          setShowDownloadModal(false);
        }
      })
      .catch((error) => {
        console.error('Error fetching data', error);
        clearTimeout(timeout);
        setShowDownloadModal(false);
        setErrorSuccessMsg(t("Une erreur est survenue lors de l'exportation."));
        setIsSuccess(false);
        setErrorSuccess(true);
      });
  }

  return (
    <div className="excel-export-button" onClick={exportTextExcel}>
      <DownloadModal
        open={showDownloadModal}
        handleClose={() => setShowDownloadModal(false)}
      />
      <ErrorSuccesModal
        open={errorSuccess}
        message={errorSuccessMsg}
        handleClose={() => setErrorSuccess(false)}
        success={isSuccess}
        toggle={() => setErrorSuccess(!errorSuccess)}
      />
    </div>
  );
};